/**
 * useEventTracker.js
 * Tracks user interactions for behavior analytics
 * 
 * Usage:
 *   import { useEventTracker, trackAction } from '../utils/useEventTracker';
 * 
 *   // In components - auto tracks page views
 *   useEventTracker('Dashboard');
 * 
 *   // Track specific actions
 *   trackAction('created_allocation', { teamId: 'xxx', pitchId: 'pitch-1' });
 */

import { useEffect, useRef } from 'react';
import { db, auth } from './firebase';
import { doc, setDoc, getDoc, collection, serverTimestamp } from 'firebase/firestore';

// Session management
let currentSessionId = null;
let sessionStartTime = null;
let lastEventTime = null;
let eventQueue = [];
let flushTimeout = null;

// Cache user info to avoid repeated Firestore reads
let cachedUserInfo = null;
let userInfoPromise = null;

// Configuration
const CONFIG = {
  SESSION_TIMEOUT: 30 * 60 * 1000,  // 30 minutes of inactivity = new session
  FLUSH_INTERVAL: 5000,              // Batch write events every 5 seconds
  MAX_QUEUE_SIZE: 20,                // Flush if queue hits this size
};

/**
 * Generate a unique session ID
 */
const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Get or create session ID
 */
const getSessionId = () => {
  const now = Date.now();
  
  // Check if session expired (30 min inactivity)
  if (currentSessionId && lastEventTime && (now - lastEventTime > CONFIG.SESSION_TIMEOUT)) {
    currentSessionId = null;
  }
  
  // Create new session if needed
  if (!currentSessionId) {
    currentSessionId = generateSessionId();
    sessionStartTime = now;
    
    // Try to persist session ID in localStorage (not sessionStorage - persists across tabs)
    try {
      localStorage.setItem('pitchallo_session_id', currentSessionId);
      localStorage.setItem('pitchallo_session_start', sessionStartTime.toString());
    } catch (e) {
      // localStorage not available
    }
  }
  
  lastEventTime = now;
  return currentSessionId;
};

/**
 * Get current user info from Firebase Auth + Firestore
 * Returns cached data if available, fetches fresh data if not
 */
const getUserInfo = async () => {
  // Return cached info if we have it
  if (cachedUserInfo) {
    return cachedUserInfo;
  }
  
  // If we're already fetching, wait for that promise
  if (userInfoPromise) {
    return userInfoPromise;
  }
  
  // Start fetching
  userInfoPromise = (async () => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return { userId: null, clubId: null, email: null };
      }
      
      // Get clubId from user's Firestore document
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      
      cachedUserInfo = {
        userId: currentUser.uid,
        clubId: userData.clubId || null,
        email: currentUser.email || null,
      };
      
      console.log('📊 Event tracker initialized for user:', cachedUserInfo.email);
      return cachedUserInfo;
    } catch (e) {
      console.error('Error getting user info for tracking:', e);
      return { userId: null, clubId: null, email: null };
    } finally {
      userInfoPromise = null;
    }
  })();
  
  return userInfoPromise;
};

/**
 * Clear cached user info (call on logout)
 */
export const clearUserCache = () => {
  cachedUserInfo = null;
  userInfoPromise = null;
  currentSessionId = null;
  eventQueue = [];
};

/**
 * Flush event queue to Firestore
 */
const flushEvents = async () => {
  if (eventQueue.length === 0) return;
  
  const eventsToFlush = [...eventQueue];
  eventQueue = [];
  
  try {
    // Batch write all events
    const promises = eventsToFlush.map(event => {
      const eventRef = doc(collection(db, 'userEvents'));
      return setDoc(eventRef, {
        ...event,
        serverTimestamp: serverTimestamp(),
      });
    });
    
    await Promise.all(promises);
    console.log(`📊 Flushed ${eventsToFlush.length} events`);
  } catch (error) {
    console.error('Failed to flush events:', error);
    // Re-add failed events to queue (at front)
    eventQueue = [...eventsToFlush, ...eventQueue];
  }
};

/**
 * Schedule flush with debouncing
 */
const scheduleFlush = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  
  // Flush immediately if queue is large
  if (eventQueue.length >= CONFIG.MAX_QUEUE_SIZE) {
    flushEvents();
    return;
  }
  
  // Otherwise schedule
  flushTimeout = setTimeout(flushEvents, CONFIG.FLUSH_INTERVAL);
};

/**
 * Queue an event for tracking (async to handle user info fetch)
 */
const queueEvent = async (eventType, page, action, metadata = {}) => {
  const userInfo = await getUserInfo();
  const { userId, clubId, email } = userInfo;
  
  // Don't track if no user
  if (!userId) {
    console.log('📊 Event skipped - no user logged in');
    return;
  }
  
  const event = {
    // Identifiers
    userId,
    clubId,
    email,
    sessionId: getSessionId(),
    
    // Event details
    eventType,        // 'page_view', 'action', 'click'
    page,             // Current page/route name
    action,           // Action name (null for page views)
    metadata,         // Additional context
    
    // Timing
    timestamp: Date.now(),
    sessionTime: sessionStartTime ? Date.now() - sessionStartTime : 0,
    
    // Context
    url: window.location.pathname,
    referrer: document.referrer || null,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    userAgent: navigator.userAgent,
  };
  
  eventQueue.push(event);
  scheduleFlush();
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Track a page view
 * @param {string} pageName - Name of the page/screen
 * @param {object} metadata - Optional additional data
 */
export const trackPageView = (pageName, metadata = {}) => {
  queueEvent('page_view', pageName, null, metadata);
  console.log(`📍 Page view: ${pageName}`);
};

/**
 * Track a user action (button click, form submit, etc.)
 * @param {string} actionName - Name of the action (e.g., 'created_allocation')
 * @param {object} metadata - Optional additional data
 */
export const trackAction = (actionName, metadata = {}) => {
  // Get current page from URL or last tracked page
  const currentPage = metadata.page || window.location.pathname;
  queueEvent('action', currentPage, actionName, metadata);
  console.log(`⚡ Action: ${actionName}`, metadata);
};

/**
 * Track a click event
 * @param {string} elementName - Name/identifier of clicked element
 * @param {object} metadata - Optional additional data
 */
export const trackClick = (elementName, metadata = {}) => {
  const currentPage = metadata.page || window.location.pathname;
  queueEvent('click', currentPage, elementName, metadata);
};

/**
 * Track navigation between pages
 * @param {string} fromPage - Page navigating from
 * @param {string} toPage - Page navigating to
 */
export const trackNavigation = (fromPage, toPage) => {
  queueEvent('navigation', fromPage, `navigate_to_${toPage}`, { toPage });
};

/**
 * React hook for automatic page view tracking
 * @param {string} pageName - Name of the current page
 * @param {object} metadata - Optional additional data
 */
export const useEventTracker = (pageName, metadata = {}) => {
  const hasTracked = useRef(false);
  const previousPage = useRef(null);
  
  useEffect(() => {
    // Track page view on mount (only once)
    if (!hasTracked.current && pageName) {
      // Track navigation if we have a previous page
      if (previousPage.current && previousPage.current !== pageName) {
        trackNavigation(previousPage.current, pageName);
      }
      
      trackPageView(pageName, metadata);
      hasTracked.current = true;
      previousPage.current = pageName;
    }
    
    // Reset on unmount so re-mounting tracks again
    return () => {
      hasTracked.current = false;
    };
  }, [pageName]);
  
  // Return tracking functions for use in component
  return {
    trackAction: (action, meta = {}) => trackAction(action, { ...meta, page: pageName }),
    trackClick: (element, meta = {}) => trackClick(element, { ...meta, page: pageName }),
  };
};

/**
 * Flush events immediately (call before page unload)
 */
export const flushEventsNow = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushEvents();
};

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushEventsNow);
  window.addEventListener('pagehide', flushEventsNow);
}

// ============================================================================
// PREDEFINED ACTION NAMES (for consistency)
// ============================================================================

export const ACTIONS = {
  // Allocations
  ALLOCATION_CREATED: 'allocation_created',
  ALLOCATION_EDITED: 'allocation_edited',
  ALLOCATION_DELETED: 'allocation_deleted',
  ALLOCATION_VIEWED: 'allocation_viewed',
  RECURRING_CREATED: 'recurring_allocation_created',
  
  // Teams
  TEAM_CREATED: 'team_created',
  TEAM_EDITED: 'team_edited',
  TEAM_DELETED: 'team_deleted',
  
  // Facilities
  FACILITY_CREATED: 'facility_created',
  FACILITY_EDITED: 'facility_edited',
  FACILITY_DELETED: 'facility_deleted',
  
  // Pitches
  PITCH_ADDED: 'pitch_added',
  PITCH_EDITED: 'pitch_edited',
  PITCH_DELETED: 'pitch_deleted',
  
  // Sharing
  SHARE_LINK_GENERATED: 'share_link_generated',
  SHARE_LINK_COPIED: 'share_link_copied',
  INVITE_SENT: 'invite_sent',
  
  // Reports
  REPORT_VIEWED: 'report_viewed',
  REPORT_EXPORTED: 'report_exported',
  
  // Settings
  SETTINGS_CHANGED: 'settings_changed',
  PROFILE_UPDATED: 'profile_updated',
  
  // Auth
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGNUP: 'signup',
  
  // Navigation
  MENU_OPENED: 'menu_opened',
  MENU_ITEM_CLICKED: 'menu_item_clicked',
  WEEK_CHANGED: 'week_changed',
  DATE_CHANGED: 'date_changed',
  
  // Errors
  ERROR_OCCURRED: 'error_occurred',
  FORM_VALIDATION_FAILED: 'form_validation_failed',
};

// ============================================================================
// PREDEFINED PAGE NAMES (for consistency)
// ============================================================================

export const PAGES = {
  LOGIN: 'Login',
  SIGNUP: 'Signup',
  DASHBOARD: 'Dashboard',
  ALLOCATIONS: 'Allocations',
  CREATE_ALLOCATION: 'Create Allocation',
  EDIT_ALLOCATION: 'Edit Allocation',
  TEAMS: 'Teams',
  ADD_TEAM: 'Add Team',
  EDIT_TEAM: 'Edit Team',
  PITCHES: 'Pitches',
  ADD_PITCH: 'Add Pitch',
  SETTINGS: 'Settings',
  SHARE_SETTINGS: 'Share Settings',
  REPORTS: 'Reports',
  WEATHER: 'Weather',
  PROFILE: 'Profile',
  BILLING: 'Billing',
  HELP: 'Help',
  OWNER_DASHBOARD: 'Owner Dashboard',
  ONBOARDING_DASHBOARD: 'Onboarding Dashboard',
  ACTIVE_USERS_DASHBOARD: 'Active Users Dashboard',
  BEHAVIOR_ANALYTICS: 'Behavior Analytics',
};

export default useEventTracker;
